/** @module others/mediaViews */

import { findDeep, hooks, inViewPort } from '../util';
import { draggable } from '../util/dragdrop';
import wp from 'wp';
import { filter } from './filter';
import $ from 'jquery';
import { isAttachmentsGalleryEdit } from '../hooks/modal';

/**
 * The attachments browser selectors.hooks
 */
export const BROWSER_SELECTOR = '.attachments-browser';

/**
 * This deferred promise is resolved when the first attachments browser'
 * toolbar is created with the RML filter.
 */
export const firstCreatedToolbar = $.Deferred();

/**
 * Restores the selected id from the _rmlFolder property of the given context.
 * 
 * @param {module:AppTree~AppTree} tree The tree instance
 * @param {object} _rmlFolder The _rmlFolder property
 * @param {function} [callback] Optional callback resolved with the selected node
 */
export function restoreMediaViewSelection(tree, _rmlFolder, callback) {
	if (_rmlFolder !== undefined && tree) {
		// Detect, if the folder is available, if yes, select it if not yet
		const { store } = tree.props,
			node = store.getTreeItemById(_rmlFolder);
		if (node && node.visible && store.selectedId !== _rmlFolder) {
			node.setter(node => { node.selected = true; });
		}
		
		// If the folder is no longer available, force 'All' files to be selected
		if (!node || !node.$visible) {
			tree.handleSelect('all');
		}else{
			callback && callback(node);
		}
	}
}

/**
 * Modify the media-views.js components (Backbone) in a way
 * to make them compatible with the AIOT component.
 */
export default function() {
    if (!findDeep(window, 'wp.media.view.Attachment.Library')) {
    	return false;
    }
    
    // Fix bug with uploads.php?item=:item route when refreshing the page
    //const oldShowItem = wp.media.view.MediaFrame.Manage.Router.prototype.showItem;
    //wp.media.view.MediaFrame.Manage.Router.prototype.showItem = function() {
    //	const args = arguments;
    //	if (wp.media.frames.browse) {
    //		return oldShowItem.apply(this, );
    //	}
    //	setTimeout(() => wp.media.view.MediaFrame.Manage.Router.prototype.showItem.apply(this, args), 50);
    //};
    
    // Create filter
    const RMLFilter = wp.media.view.AttachmentFilters.RML = wp.media.view.AttachmentFilters.extend(filter);
    
    // Hold selectedId to select previously selected id
    const Modal = wp.media.view.Modal;
    wp.media.view.Modal = wp.media.view.Modal.extend({
    	className: 'rml-modal',
    	
    	// Add class and backboneView so we can find it via DOM, see getModalControllerOf in modal.js
    	initialize() {
			Modal.prototype.initialize.apply(this, arguments);
			this.$el.data('backboneView', this);
    	},
    	
    	close() {
    		Modal.prototype.close.apply(this, arguments);
    		const { $RmlAppTree } = this.controller;
    		this._rmlFolder = $RmlAppTree ? $RmlAppTree.props.store.selectedId : undefined;
    	},
    	
    	open() {
    		const { $RmlAppTree } = this.controller;
    		$RmlAppTree && restoreMediaViewSelection($RmlAppTree, this._rmlFolder);
    		Modal.prototype.open.apply(this, arguments);
    	}
    });
    
    // Allow rml orderby
    wp.media.model.Query.orderby.allowed.push('rml');
    
    const createComporator = (attachments, collection, fallbackComparator) => function(a, b, c) {
    	const { $RmlAppTree } = attachments.controller;
		let selectedId;
		if ($RmlAppTree && $RmlAppTree.props && (selectedId = $RmlAppTree.getSelectedId())
			&& selectedId === 'all') {
			return fallbackComparator.apply(this, arguments);
		}
		
		let aO, bO;
		if (collection.props.get('orderby') === 'rml' && (aO = a.attributes.rmlGalleryOrder)
			&& (bO = b.attributes.rmlGalleryOrder) && aO !== -1 && bO !== -1) {
	        if (aO < bO) {
	            return -1;
	        }else if (aO > bO) {
	            return 1;
	        }
	        return 0;
		}else if (typeof fallbackComparator === 'function') {
			return fallbackComparator.apply(this, arguments);
		}
    };
    
    // Create rml orderby comparator
    const Attachments = wp.media.view.Attachments;
	wp.media.view.Attachments = wp.media.view.Attachments.extend({
		initialize() {
			Attachments.prototype.initialize.apply(this, arguments);
			
			const that = this, { collection } = this,
				{ comparator } = collection;
			
			// Disable comporator in gallery-edit mode (dialog)
			if (isAttachmentsGalleryEdit(this)) {
				collection.comparator = undefined;
				this.initSortable();
				return;
			}
			
			collection.comparator = createComporator(that, collection, comparator);
			
			// Initially load a folder from the AppTree initialSelectedId state
			const oldMore = collection.more;
			that._rmlInitialSetted = false;
			collection.more = function() {
				if (!that.views.parent) {
					return oldMore.apply(this, arguments);
				}
				const { $RmlAppTree } = that.controller,
					toolbar = that.views.parent.toolbar;
				let initialSelectedId;
				if ($RmlAppTree && $RmlAppTree.props && (initialSelectedId = $RmlAppTree.initialSelectedId)) {
					if (!that._rmlInitialSetted) {
						const model = toolbar.get('rml_folder').model;
						model.set({ rml_folder: initialSelectedId === 'all' ? '' : initialSelectedId }, { silent: false });
						that._rmlInitialSetted = true;
					}
					return oldMore.apply(this, arguments);
				}
				return $.Deferred().resolveWith(that).promise();
			};
		},
		
		/**
		 * Override the default behavior when scrolling is relative to the document
		 * height: upload.php. Instead of calculating with the scroll bottom use
		 * the view-port approach.
		 * 
		 * @internal
		 */
		scroll() {
			let el = this.options.scrollElement,
				overrideDefault = el === document;
				
			// The scroll event occurs on the document, but the element
			// that should be checked is the last grid item
			if (overrideDefault && !this.$el.hasClass('rml-loading')) {
				el = this.$el.children(':last');
				if (!$(el).is(':visible') || !this.collection.hasMore()) {
					return;
				}
				
				if (inViewPort(el, true)) {
					this.$el.addClass('rml-loading');
					this.collection.more().done(() => {
						this.$el.removeClass('rml-loading');
						this.scroll();
					});
				}
			}else{
				Attachments.prototype.scroll.apply(this, arguments);
			}
		}
	});
    
    // Call a grid render for all new generated items
	const oldRender = wp.media.view.Attachment.Library.prototype.render;
	wp.media.view.Attachment.Library.prototype.render = function() {
		oldRender.apply(this, arguments);
		const { $RmlAppTree } = this.controller;
		
		/**
         * Fired when an attachments browser item is rendered.
         * 
         * @event module:util/hooks#attachmentsBrowser/item/rendered
         * @param {jQuery} $el The element
         * @param {object} model The backbone model
         * @param {object} appTree The app tree instance
         * @this wp.media.view.Attachment.Library
         */
		hooks.call('attachmentsBrowser/item/rendered', [ this.$el, this.model, $RmlAppTree ], this);
	};
    
    // Modify attachments browser
    let timeoutReloadCount;
    const AttachmentsBrowser = wp.media.view.AttachmentsBrowser;
    wp.media.view.AttachmentsBrowser = wp.media.view.AttachmentsBrowser.extend({
		initialize() {
			AttachmentsBrowser.prototype.initialize.apply(this, arguments);
			
			// Events for attachments browsers collections
			let timeout;
			this.collection.on('change reset add remove', () => {
                clearTimeout(timeout);
                timeout = setTimeout(() => {
                    // Merged collection change
                    const { $RmlAppTree } = this.controller;
                    if ($RmlAppTree) {
	                    draggable($RmlAppTree);
	                    
	                    /**
				         * Fired when the collection of attachments browser changes.
				         * 
				         * @event module:util/hooks#attachmentsBrowser/collection/change
				         * @param {object} appTree The app tree instance
				         * @this wp.media.view.AttachmentsBrowser
				         */
	                    hooks.call('attachmentsBrowser/collection/change', [ $RmlAppTree ], this);
                    }
                }, 50);
            });
            
            this.collection.on('remove', (...args) => {
            	/**
		         * Fired when an attachments browser item gets removed.
		         * 
		         * @event module:util/hooks#attachmentsBrowser/item/removed
		         * @param {mixed} args... The event arguments
		         * @this wp.media.view.AttachmentsBrowser
		         */
				hooks.call('attachmentsBrowser/item/removed', [this.controller.$RmlAppTree, ...args], this);
			});
			
			// Listen to the ajax complete to refresh the folder counts
	    	$(document).ajaxComplete((e, xhs, req) => {
	    	    try {
	    	        if (req.data.indexOf('action=delete-post') > -1) {
	    	            const { $RmlAppTree } = this.controller;
	    	            clearTimeout(timeoutReloadCount);
	    	            $RmlAppTree && (timeoutReloadCount = setTimeout(() => $RmlAppTree.fetchCounts(), 1800));
	    	        }
	    	    }catch(e) {
	    	    	// Silence is golden.
	    	    }
	    	});
		},
		
		createToolbar() {
		    AttachmentsBrowser.prototype.createToolbar.call(this);
	        this.$el.data('backboneView', this);
	        
	        // Add new toolbar
	        const obj = new RMLFilter({
				controller: this.controller,
				model: this.collection.props,
				priority: -81 // see media-views.js#7295
			}).render();
			this.toolbar.set('rml_folder', obj);
			obj.initialize();
			
			const { modal } = this.controller.options;
			if (modal) {
				/**
		         * Fired, when a new modal window is created.
		         * 
		         * @event module:util/hooks#attachmentsBrowser/modal
		         * @this wp.media.view.AttachmentsBrowser
		         */
				hooks.call('attachmentsBrowser/modal', [], this);
			}else{
				firstCreatedToolbar.resolve(this);
			}
	    },
	    
	    remove() {
	    	const { $RmlAppTree } = this.controller;
	    	$RmlAppTree && $RmlAppTree.handleDestroy();
	    	AttachmentsBrowser.prototype.remove.apply(this, arguments);
	    }
    });
    
    return true;
}

/**
 * Enhanced Media Library compatibility and layout adjustment.
 */
hooks.register('ready', () => {
	if ($('body').hasClass('eml-grid')) {
        const mediaGrid = $('#wp-media-grid'),
            offsetTop = mediaGrid.offset().top,
            fnResize = () => { mediaGrid.css('height', $(window).height() - $('#wpadminbar').height() - 10); },
            fnScroll = () => {
                const scrollTop = $(window).scrollTop();
                mediaGrid[0].style.top = (scrollTop > offsetTop ? scrollTop : 0) + 'px';
                //mediaGrid.css("top", scrollTop > offsetTop ? scrollTop - offsetTop : 0);
            };
    
        // Centerize container
        $(window).on("resize", fnResize);
        fnResize();
        
        // Scroll container
        $(window).on("scroll", fnScroll);
        fnScroll();
    }
});